import { useCallback, useContext, useEffect, useRef } from "react";

import CustomToolbar from "@/components/RichTextEditor/QuillTextEditor/customToolbar.component";
import "@/components/RichTextEditor/QuillTextEditor/quillTextEditor.css";
import { AppContext } from "@/services/AppContext/appContext";
import { cn } from "@/shadcn/lib/utils";
import { formatDueDate, removeImageFromString } from "@/utils/utils";
import ReactQuill from "react-quill-new";

type QuillTextEditorProps = {
  value: string;
  id: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  containerClassName?: string;
  className?: string;
};

const QuillTextEditor: React.FC<QuillTextEditorProps> = ({
  value,
  id,
  onChange,
  readOnly = false,
  placeholder = "Type something...",
  containerClassName,
  className,
}) => {
  const quillRef = useRef<ReactQuill | null>(null);
  const { user } = useContext(AppContext);
  const containerRef = useRef<HTMLDivElement>(null);

  // Function to insert comment at cursor position
  const insertComment = useCallback(() => {
    if (!quillRef.current || readOnly) return;

    const editor = quillRef.current.getEditor();

    const currentDate = formatDueDate(new Date(), true);

    const insertCommentText = `${currentDate} - ${user.first_name} ${user.surname}:\n`;

    // Get current selection range
    const range = editor.getSelection();

    if (range) {
      // Insert the comment text at the cursor position
      editor.insertText(range.index, insertCommentText);
      // Apply bold formatting to the inserted text
      editor.formatText(range.index, insertCommentText.length, "bold", true);
    } else {
      // If no selection, insert at the end
      const insertIndex = editor.getLength() - 1;
      editor.insertText(insertIndex, insertCommentText);
      editor.formatText(insertIndex, insertCommentText.length, "bold", true);
    }
  }, [quillRef, user, readOnly]);

  // Set up the custom button handler after component mounts
  useEffect(() => {
    if (typeof document !== "undefined") {
      const commentButton = document.getElementById(`insert-comment-${id}`);
      if (commentButton) {
        commentButton.addEventListener("click", insertComment);
      }

      // Cleanup event listener on unmount
      return () => {
        if (commentButton) {
          commentButton.removeEventListener("click", insertComment);
        }
      };
    }
  }, [insertComment, id]);

  const modules = {
    toolbar: {
      container: `#toolbar-${id}`,
    },
  };

  useEffect(() => {
    function handleLinkClick(e: MouseEvent) {
      if (e.button === 2) {
        // if to element is anchor, open href in new tab
        if (e.target instanceof HTMLAnchorElement) {
          // prevent default context menu
          e.preventDefault();
          window.open(e.target.href, "_blank");
        }
      }
    }

    document.addEventListener("mousedown", (e) => {
      handleLinkClick(e);
    });
    return () => {
      // cleanup, remove event listener
      document.removeEventListener("mousedown", (e) => {
        handleLinkClick(e);
      });
    };
  }, []);

  function handleChange(value: string): void {
    onChange(removeImageFromString(value));
  }

  return (
    <div
      className={cn(
        "rich-text-editor rounded-md border w-full grid",
        containerClassName
      )}
      ref={containerRef}
    >
      <CustomToolbar id={id} />
      <ReactQuill
        id={id}
        readOnly={readOnly}
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={handleChange}
        modules={modules}
        className={cn(
          "max-h-96 overflow-y-auto overflow-x-hidden break-words text-wrap w-full",
          className
        )}
        placeholder={placeholder}
      />
    </div>
  );
};

export default QuillTextEditor;
